import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { supabase } from '../lib/supabase';
import { Clock, ArrowRight, Loader2 } from 'lucide-react';

interface TeaserPost {
  id: string;
  slug: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  featured_image: string | null;
  reading_time_minutes: number;
  published_at: string;
  category_id: string | null;
}

interface CategoryInfo {
  id: string;
  name_es: string;
  name_en: string;
}

const BlogTeaser: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language].blog;

  const [posts, setPosts] = useState<TeaserPost[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [postsRes, catRes] = await Promise.all([
        supabase
          .from('blog_posts')
          .select('id, slug, title_es, title_en, excerpt_es, excerpt_en, featured_image, reading_time_minutes, published_at, category_id')
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(3),
        supabase
          .from('blog_categories')
          .select('id, name_es, name_en')
      ]);

      if (postsRes.data) setPosts(postsRes.data);
      if (catRes.data) setCategories(catRes.data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryName = (catId: string | null) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return '';
    return language === 'es' ? cat.name_es : cat.name_en;
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#115d7b' }} />
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t.latestInsights}</h2>
            <p className="text-gray-600">{t.heroSubtitle}</p>
          </div>
          <a
            href="/blog"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
            style={{ color: '#115d7b' }}
          >
            {t.viewAllPosts}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}} />
        <div className="flex overflow-x-auto md:grid md:grid-cols-3 gap-6 max-w-7xl mx-auto snap-x snap-mandatory scrollbar-none pb-6 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
          {posts.map(post => (
            <a
              key={post.id}
              href={`/blog/${post.slug}`}
              className="w-[85vw] md:w-full flex-shrink-0 md:flex-shrink snap-start group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white border border-gray-100 hover:-translate-y-1"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={post.featured_image || 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1'}
                  alt={language === 'es' ? post.title_es : post.title_en}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {post.category_id && (
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#ff8c00' }}>
                      {getCategoryName(post.category_id)}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                  {language === 'es' ? post.title_es : post.title_en}
                </h3>
                <p className="text-gray-500 text-sm mb-4 line-clamp-2">
                  {language === 'es' ? post.excerpt_es : post.excerpt_en}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{formatDate(post.published_at)}</span>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.reading_time_minutes} {t.minutesRead}
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="text-center mt-10 md:hidden">
          <a
            href="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border-2 transition-all hover:shadow-lg"
            style={{ borderColor: '#115d7b', color: '#115d7b' }}
          >
            {t.viewAllPosts}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BlogTeaser;
